import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, ChevronDown, CalendarDays } from "lucide-react";

const DISTANCES = ["5K", "10K", "15K", "Half Marathon", "Marathon"];
const WEEKS = [4, 8, 12, 16];
const LEVELS = ["beginner", "intermediate", "advanced"];

export const TrainingPlans = () => {
  const [distance, setDistance] = useState("5K");
  const [weeks, setWeeks] = useState("8");
  const [level, setLevel] = useState("beginner");
  const [generating, setGenerating] = useState(false);
  const [plans, setPlans] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = () => api.get("/running/plans").then(({ data }) => setPlans(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/running/plan", { distance, weeks: parseInt(weeks, 10), level });
      toast.success("Training plan created");
      setPlans((p) => [data, ...p]);
      setOpenId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate plan");
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/running/plans/${id}`);
      setPlans((p) => p.filter((x) => x.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  const selectCls = "bg-card border-border h-11";

  return (
    <div className="pb-28" data-testid="training-plans">
      <h3 className="font-display text-lg font-bold mb-1">Real Deal Training Plans</h3>
      <p className="text-muted-foreground text-sm mb-4">Generate a week-by-week running plan in miles, built for your level.</p>

      <Card className="rounded-2xl bg-card border-border p-4 space-y-3 mb-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Race distance</Label>
            <Select value={distance} onValueChange={setDistance}>
              <SelectTrigger data-testid="plan-distance" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border text-foreground">
                {DISTANCES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Length</Label>
            <Select value={weeks} onValueChange={setWeeks}>
              <SelectTrigger data-testid="plan-weeks" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border text-foreground">
                {WEEKS.map((w) => <SelectItem key={w} value={String(w)}>{w} weeks</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Experience level</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="plan-level" className={`${selectCls} capitalize`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border text-foreground">
              {LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="generate-plan" onClick={generate} disabled={generating} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold">
          {generating ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Building your plan…</> : <><Sparkles className="h-5 w-5 mr-2" /> Generate plan</>}
        </Button>
      </Card>

      {plans.length === 0 ? (
        <div className="flex flex-col items-center py-6">
          <CalendarDays className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-sm">No plans yet. Generate your first training block above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {plans.map((pl) => (
            <Card key={pl.id} data-testid={`plan-${pl.id}`} className="rounded-2xl bg-card border-border overflow-hidden">
              <Collapsible open={openId === pl.id} onOpenChange={(o) => setOpenId(o ? pl.id : null)}>
                <div className="flex items-center justify-between p-4">
                  <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left" data-testid={`plan-toggle-${pl.id}`}>
                    <div className="h-10 w-10 rounded-xl bg-[#ccff0c]/15 flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="h-5 w-5 text-[#ccff0c]" />
                    </div>
                    <div>
                      <p className="font-semibold">{pl.weeks}-week {pl.distance}</p>
                      <p className="text-xs text-muted-foreground capitalize">{pl.level} · {new Date(pl.created_at).toLocaleDateString()}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${openId === pl.id ? "rotate-180" : ""}`} />
                  </CollapsibleTrigger>
                  <button data-testid={`plan-delete-${pl.id}`} onClick={() => remove(pl.id)} className="ml-3 text-muted-foreground hover:text-red-400 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <CollapsibleContent>
                  <div className="px-4 pb-4">
                    <div className="rounded-xl bg-card border border-border p-4 max-h-[420px] overflow-y-auto rd-scrollbar">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{pl.content}</p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TrainingPlans;
